// Resumen ejecutivo del pedido: la única zona de la pantalla con bloques.
//
// Se lee en dos pasos, de mayor a menor compromiso:
//   1. el AVANCE, a todo lo ancho — la cifra que define el pedido;
//   2. la TIRA DE INSTRUMENTOS — atención, cargas, operación y finanzas, cuatro
//      lecturas dentro de un solo marco y separadas por un filete.
// Y al pie, la metadata administrativa en gris.
//
// La composición cambió a propósito. Antes eran CINCO bloques con marco propio
// repartidos en una rejilla de 12 columnas, y con datos reales eso se leía como
// una plantilla de dashboard: cinco cajas del mismo tamaño y ninguna jerarquía.
// Dos elementos con peso —una declaración y un panel— dicen lo mismo y se leen
// antes. También desapareció la franja de «situación actual»: sus cinco cifras
// estaban todas repetidas en los bloques de abajo.
//
// REGLA QUE NO SE PUEDE ROMPER: el único ratio «de pedido» es servida/pedida.
// Picking y facturación son indicadores INDEPENDIENTES —una línea puede estar
// picada al 73 % y servida al 0 % mientras no se expide—, así que se pintan
// aparte, con su denominador escrito al lado, y nunca se apilan sobre el
// principal ni se suman entre sí.
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { totalPicking, type ResumenCarga } from '@/utils/cargasPicking'
import { totalFacturado } from '@/utils/facturasPedido'
import { fmtDate, fmtEur, fmtQty } from '../formato'
import {
  apagadoCls, avisoCls, bordeTenueCls, cifraGrandeCls, rotuloCls, tenueCls, textoCls,
} from './estilos'
import type {
  CargaLinea, PedidoVentaCabeceraVM, PedidoVentaLinea, SolicitudCarga,
} from '@/types'

export interface AlertaPedido {
  clave: string
  texto: string
  tono: 'ambar' | 'rojo'
}

interface Props {
  dark: boolean
  cabecera: PedidoVentaCabeceraVM
  lineas: PedidoVentaLinea[]
  cargas: CargaLinea[]
  porCarga: ResumenCarga[]
  solicitudes: SolicitudCarga[]
  qtySinLanzar: number
  alertas: AlertaPedido[]
  isEmpresaAll: boolean
  onIrACargas: () => void
}

export function ResumenBento({
  dark, cabecera: c, lineas, cargas, porCarga, solicitudes, qtySinLanzar, alertas,
  isEmpresaAll, onIrACargas,
}: Props) {
  const { t } = useTranslation()

  const pctServido = c.qtyPedida > 0 ? (c.qtyServida / c.qtyPedida) * 100 : 0
  const picking = totalPicking(cargas)
  const facturado = totalFacturado(lineas)
  const numFacturas = new Set(
    lineas.map(l => l.factura?.trim()).filter(Boolean) as string[],
  ).size
  const expedidas = porCarga.filter(x => x.expedida).length
  const enAlmacen = porCarga.length - expedidas
  const ud = t('salesOrders.picking.units')

  // Días de retraso, sólo si la entrega ya pasó y queda algo por servir: un
  // pedido completado tarde ya no pide ninguna acción.
  const diasDeRetraso = (() => {
    if (!c.fechaEntrega || c.qtyPendiente <= 0) return null
    const hoy = new Date().toISOString().slice(0, 10)
    if (c.fechaEntrega >= hoy) return null
    const dias = Math.floor((Date.parse(hoy) - Date.parse(c.fechaEntrega)) / 86_400_000)
    return dias > 0 ? dias : null
  })()

  /** Fila etiqueta → valor de los instrumentos. */
  const fila = (etiqueta: string, valor: string, tono?: 'fuerte') => (
    <div className="flex items-baseline justify-between gap-3">
      <span className={`text-[11px] ${apagadoCls(dark)}`}>{etiqueta}</span>
      <span className={tono === 'fuerte'
        ? `text-[15px] font-bold tabular-nums ${dark ? 'text-slate-50' : 'text-slate-900'}`
        : `text-[13px] font-semibold tabular-nums ${textoCls(dark)}`}>
        {valor}
      </span>
    </div>
  )

  /** Cifra destacada: etiqueta encima, cifra, y el denominador debajo.
   *
   *  Ese orden importa. Con la etiqueta al final, «430.944 ud / sobre 430.944
   *  lanzadas / Picking preparado» se lee al revés y hay que volver atrás para
   *  saber de qué era la cifra. */
  const destacado = (
    etiqueta: string, valor: string, unidad: string, contexto?: string, titulo?: string,
  ) => (
    <div className="flex flex-col gap-1">
      <span className={`text-[11px] ${apagadoCls(dark)}`}>{etiqueta}</span>
      <span className={`text-[23px] font-bold tabular-nums leading-none ${dark ? 'text-slate-50' : 'text-slate-900'}`} title={titulo}>
        {valor}
        {unidad && <span className={`ml-1 text-[12px] font-normal ${tenueCls(dark)}`}>{unidad}</span>}
      </span>
      {contexto && <span className={`text-[11px] leading-snug ${tenueCls(dark)}`}>{contexto}</span>}
    </div>
  )

  const filete = dark ? 'border-white/8' : 'border-slate-200'

  /** Filetes de cada celda de la tira, por posición.
   *
   *  Escritos a mano y no con `[&>*+*]`: la tira pasa de 1 a 2 y a 4 columnas, y
   *  en cada reparto el vecino de arriba y el de la izquierda son otros. Todas
   *  las clases van literales porque Tailwind lee el código fuente y no
   *  generaría una compuesta en tiempo de ejecución. */
  const BORDES_CELDA = [
    '',
    'border-t sm:border-t-0 sm:border-l',
    'border-t xl:border-t-0 xl:border-l',
    'border-t sm:border-l xl:border-t-0',
  ]

  /** Celda de la tira de instrumentos.
   *
   *  Sin marco propio: el marco es de la tira entera y las celdas se separan con
   *  un filete. Cuatro cajas independientes en rejilla es justo el aspecto de
   *  plantilla que esta pantalla evita. */
  const instrumento = (
    i: number, titulo: string, cuerpo: ReactNode, tono?: 'alerta',
  ) => (
    <div className={`flex flex-col gap-2.5 p-4 transition-colors duration-200 ${filete} ${BORDES_CELDA[i]} ${
      tono === 'alerta'
        ? dark ? 'bg-red-500/[0.14]' : 'bg-red-50'
        : dark ? 'hover:bg-white/[0.03]' : 'hover:bg-slate-50/70'
    }`}>
      <span className={tono === 'alerta'
        ? `inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${dark ? 'text-red-300' : 'text-red-700'}`
        : rotuloCls(dark)}>
        {tono === 'alerta' && (
          <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 1.8L15 14H1L8 1.8z" /><path d="M8 6.5v3.2" /><path d="M8 11.8h.01" />
          </svg>
        )}
        {titulo}
      </span>
      {cuerpo}
    </div>
  )

  return (
    <div className="flex flex-col gap-3">
      {/* ── El avance, a todo lo ancho ────────────────────────────────────────
          La cifra a la izquierda con su barra —que es la cifra dibujada— y la
          escala a la derecha, separadas por un filete. Ocupa el ancho en vez de
          apilarse, que era lo que dejaba medio bloque en blanco. */}
      <section className={`flex flex-col gap-4 rounded-xl border p-5 ${
        dark ? 'border-white/8 bg-white/[0.05]' : 'border-slate-200 bg-slate-50/60'
      }`}>
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <span className={rotuloCls(dark)}>{t('salesOrders.detalle.avancePedido')}</span>
          {(c.fechaPedido || c.fechaEntrega) && (
            <span className="flex items-baseline gap-2.5 flex-wrap">
              {c.fechaPedido && (
                <span className={`text-[11px] ${tenueCls(dark)}`}>
                  {t('salesOrders.col.orderDate')}{' '}
                  <span className={`font-semibold tabular-nums ${apagadoCls(dark)}`}>{fmtDate(c.fechaPedido)}</span>
                </span>
              )}
              {c.fechaPedido && c.fechaEntrega && <span className={tenueCls(dark)}>→</span>}
              {c.fechaEntrega && (
                <span className={`text-[11px] ${tenueCls(dark)}`}>
                  {t('salesOrders.col.deliveryDate')}{' '}
                  <span className={`font-semibold tabular-nums ${apagadoCls(dark)}`}>{fmtDate(c.fechaEntrega)}</span>
                </span>
              )}
              {/* Distintivo y no texto suelto: en una fila de fechas en gris,
                  un rojo del mismo cuerpo se lee como una fecha más. */}
              {diasDeRetraso !== null && (
                <span className={avisoCls('rojo', 'suave', dark)}>
                  {t('salesOrders.detalle.retraso', { count: diasDeRetraso })}
                </span>
              )}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:gap-8">
          <div className="flex items-baseline gap-2.5 shrink-0 lg:w-[34%]">
            <span className={`${cifraGrandeCls(dark)} text-[64px] tracking-[-0.04em]`}>
              {pctServido.toLocaleString('es-ES', { maximumFractionDigits: 0 })}%
            </span>
            <span className={`text-[14px] font-medium ${apagadoCls(dark)}`}>
              {t('salesOrders.detalle.servido')}
            </span>
          </div>

          {/* La escala se lee como una progresión —pedida → servida → pendiente—,
              así que va en fila y separada con filetes. */}
          <dl className={`grid grid-cols-3 flex-1 lg:border-l lg:pl-8 ${filete}`}>
            {([
              ['salesOrders.line.qtyOrdered', c.qtyPedida, ''],
              ['salesOrders.line.qtyServed', c.qtyServida, dark ? 'text-emerald-400' : 'text-emerald-600'],
              ['salesOrders.line.qtyPending', c.qtyPendiente, ''],
            ] as const).map(([clave, valor, tono], i) => (
              <div
                key={clave}
                className={`flex flex-col gap-1.5 ${filete} ${i > 0 ? 'border-l pl-5' : ''}`}
              >
                <dt className={rotuloCls(dark)}>{t(clave)}</dt>
                <dd className={`text-[24px] font-bold tabular-nums leading-none ${
                  tono || (dark ? 'text-slate-50' : 'text-slate-900')
                }`}>
                  {fmtQty(valor)}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* La barra, a todo lo ancho y al pie del bloque: es la cifra dibujada,
            y encajada en la columna de la izquierda se quedaba en un muñón al
            lado de un número de 64 px. Aquí hace de línea de base. */}
        <div
          className={`h-1.5 rounded-full overflow-hidden ${dark ? 'bg-white/10' : 'bg-slate-200'}`}
          title={t('salesOrders.line.servedTooltip', {
            servida: fmtQty(c.qtyServida),
            pedida: fmtQty(c.qtyPedida),
            pendiente: fmtQty(c.qtyPendiente),
          })}
        >
          <div
            className={`pv-barra-in h-full rounded-full ${dark ? 'bg-sky-400' : 'bg-sky-600'}`}
            style={{ width: `${Math.min(100, Math.max(0, pctServido))}%` }}
          />
        </div>
      </section>

      {/* ── Tira de instrumentos ──────────────────────────────────────────────
          Un solo marco y cuatro lecturas separadas por un filete. */}
      <section className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 rounded-xl border overflow-hidden ${
        dark ? 'border-white/8 bg-white/[0.03]' : 'border-slate-200 bg-white'
      }`}>
        {instrumento(
          0,
          t(alertas.length > 0 ? 'salesOrders.detalle.requiereAtencion' : 'salesOrders.detalle.alertas'),
          alertas.length === 0 ? (
            <p className={`flex items-center gap-1.5 text-[13px] font-medium ${dark ? 'text-emerald-400' : 'text-emerald-700'}`}>
              <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8.5l3.5 3.5L13 5" />
              </svg>
              {t('salesOrders.detalle.sinIncidencias')}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {alertas.map(a => (
                <li
                  key={a.clave}
                  className={`flex items-start gap-2 text-[12.5px] font-semibold leading-snug ${
                    a.tono === 'rojo'
                      ? dark ? 'text-red-100' : 'text-red-900'
                      : dark ? 'text-amber-100' : 'text-amber-900'
                  }`}
                >
                  <span className={`mt-1.5 w-1.5 h-1.5 shrink-0 rounded-full ${
                    a.tono === 'rojo' ? 'bg-red-500' : 'bg-amber-500'
                  }`} />
                  {a.texto}
                </li>
              ))}
            </ul>
          ),
          alertas.length > 0 ? 'alerta' : undefined,
        )}

        {instrumento(1, t('salesOrders.detalle.cargasCol'), (
          <>
            <button
              type="button"
              onClick={onIrACargas}
              className={`self-start ${cifraGrandeCls(dark)} cursor-pointer transition-colors duration-150 hover:text-sky-500`}
              title={t('salesOrders.detalle.irACargas')}
            >
              {porCarga.length}
            </button>

            {/* Proporción expedido / en almacén: dos tramos disjuntos que suman
                exactamente el total de cargas, así que la barra es honesta. */}
            {porCarga.length > 0 && (
              <div className={`pv-barra-in flex h-1 rounded-full overflow-hidden ${dark ? 'bg-white/10' : 'bg-slate-200'}`}>
                {expedidas > 0 && (
                  <div
                    className={dark ? 'bg-slate-500' : 'bg-slate-400'}
                    style={{ width: `${(expedidas / porCarga.length) * 100}%` }}
                  />
                )}
                {enAlmacen > 0 && (
                  <div className="bg-amber-500" style={{ width: `${(enAlmacen / porCarga.length) * 100}%` }} />
                )}
              </div>
            )}

            <ul className="flex flex-col gap-1">
              {expedidas > 0 && (
                <li className={`text-[11px] ${apagadoCls(dark)}`}>
                  {t('salesOrders.detalle.cargasExpedidas', { count: expedidas })}
                </li>
              )}
              {/* Con punto ámbar, el mismo lenguaje que la lista de alertas: lo
                  que sigue en almacén es lo único de este bloque sobre lo que se
                  puede actuar. */}
              {enAlmacen > 0 && (
                <li className={`flex items-center gap-1.5 text-[11px] font-semibold ${dark ? 'text-amber-300' : 'text-amber-700'}`}>
                  <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-amber-500" />
                  {t('salesOrders.detalle.cargasEnAlmacen', { count: enAlmacen })}
                </li>
              )}
              {solicitudes.length > 0 && (
                <li className={`flex items-center gap-1.5 text-[11px] font-semibold ${dark ? 'text-amber-300' : 'text-amber-700'}`}>
                  <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-amber-500" />
                  {t('salesOrders.detalle.alertaSolicitudes', { count: solicitudes.length })}
                </li>
              )}
              {porCarga.length === 0 && solicitudes.length === 0 && (
                <li className={`text-[11px] ${tenueCls(dark)}`}>{t('salesOrders.nuevaCarga.sinCargas')}</li>
              )}
            </ul>
          </>
        ))}

        {/* Operación y Finanzas van seguidas y con la misma estructura —cifra,
            contexto, cierre— para poder compararlas de un vistazo sin que
            ninguna cifra se sume con la del otro instrumento. */}
        {instrumento(2, t('salesOrders.detalle.logistico'), (
          <>
            {destacado(
              t('salesOrders.detalle.pickingPreparado'),
              fmtQty(picking.picking),
              ud,
              t('salesOrders.detalle.sobreLanzadas', { total: fmtQty(picking.solicitada) }),
              t('salesOrders.picking.totalTooltip', {
                picking: fmtQty(picking.picking),
                solicitada: fmtQty(picking.solicitada),
                pendiente: fmtQty(picking.pendiente),
              }),
            )}
            <div className={`mt-auto pt-2.5 border-t ${bordeTenueCls(dark)}`}>
              {fila(t('salesOrders.line.qtyPendingLaunch'), `${fmtQty(qtySinLanzar)} ${ud}`, 'fuerte')}
            </div>
          </>
        ))}

        {instrumento(3, t('salesOrders.detalle.financiero'), (
          <>
            {/* El pendiente manda: es la cifra que decide si el pedido preocupa. */}
            {destacado(
              t('salesOrders.col.pendingAmount'),
              fmtEur(c.importePendiente),
              '',
              t('salesOrders.detalle.facturadoPct', {
                pct: facturado.pct.toLocaleString('es-ES', { maximumFractionDigits: 0 }),
              }),
            )}
            {/* `importeTotal` e `importeFacturado` se pintan como dos cifras
                independientes y NO como un total que se descompone: en el origen
                no guardan esa relación (medido: en 26APPV000007 coinciden y los
                dos son menores que el pendiente). */}
            <div className={`mt-auto flex flex-col gap-1.5 pt-2.5 border-t ${bordeTenueCls(dark)}`}>
              {fila(t('salesOrders.col.amount'), fmtEur(c.importeTotal))}
              {fila(t('salesOrders.detalle.importeFacturado'), fmtEur(facturado.importe))}
              {fila(t('salesOrders.detalle.facturasEtiqueta'), String(numFacturas))}
            </div>
          </>
        ))}
      </section>

      {/* ── Información del pedido ────────────────────────────────────────────
          Sin pestaña propia: eran cinco campos, y dedicarles una pantalla
          entera dejaba más vacío que información. Metadata, no sección. */}
      <InformacionPedido dark={dark} cabecera={c} isEmpresaAll={isEmpresaAll} />
    </div>
  )
}

function InformacionPedido({
  dark, cabecera: c, isEmpresaAll,
}: {
  dark: boolean
  cabecera: PedidoVentaCabeceraVM
  isEmpresaAll: boolean
}) {
  const { t } = useTranslation()

  // Sin cliente, estado ni fechas: ya están en la cabecera, y repetirlos aquí
  // sólo alarga la línea sin añadir nada.
  const campos: [string, string | undefined][] = [
    [t('salesOrders.detalle.codigoCliente'), c.custaccount],
    [t('salesOrders.col.customerRef'), c.customerRef],
    [t('common.company'), isEmpresaAll ? c.empresa : undefined],
    [t('common.family'), c.familia],
    [t('salesOrders.detalle.subfamilia'), c.subfamilia],
    [t('salesOrders.detalle.moneda'), c.moneda],
  ]
  const visibles = campos.filter(([, v]) => !!v)
  if (visibles.length === 0) return null

  return (
    <section className={`flex items-baseline gap-x-6 gap-y-2 flex-wrap pt-3 border-t ${bordeTenueCls(dark)}`}>
      <span className={rotuloCls(dark)}>{t('salesOrders.detalle.infoPedido')}</span>
      {visibles.map(([etiqueta, valor]) => (
        <span key={etiqueta} className="flex items-baseline gap-1.5">
          <span className={`text-[11px] ${tenueCls(dark)}`}>{etiqueta}</span>
          <span className={`text-[12px] font-semibold ${apagadoCls(dark)}`}>{valor}</span>
        </span>
      ))}
    </section>
  )
}
